import { computed, defineComponent, onMounted, reactive, ref } from "vue"
import { propsDef, routeDetailToRouteLocation } from "./R_SeasonManager.route"
import { Router, useRouter } from "vue-router";
import { UiOption, assertIs, exhaustiveCaseGuard, parseIntOrFail, requireNonNull, unreachable, useIziToast } from "src/helpers/utils";
import { Guid, MinSeason } from "src/interfaces/InleagueApiV1";
import { axiosAuthBackgroundInstance, axiosInstance } from "src/boot/AxiosInstances";
import { CalendarSeason, DobCutoff, SeasonForManagerView, createSeason, getSeasonForManagerView, listSeasonOptionsForManagerView, updateSeason } from "src/composables/InleagueApiV1.Seasons";
import { EditSeasonElement, SeasonFormData } from "./SeasonManager.impl";
import { FormKit } from "@formkit/vue";
import dayjs from "dayjs";
import { AxiosErrorWrapper } from "src/boot/AxiosErrorWrapper";
import { DAYJS_FORMAT_HTML_DATE, dayjsFormatOr } from "src/helpers/formatDate";
import { ReactiveReifiedPromise } from "src/helpers/ReifiedPromise";
import { SoccerBall } from "../SVGs";
import { GlobalInteractionBlockingRequestsInFlight } from "src/store/EventuallyPinia";

export default defineComponent({
  props: propsDef,
  setup(props) {
    const ready = ref(false);
    const router = useRouter();
    const iziToast = useIziToast();

    const seasonOptions = ref<UiOption<Guid | typeof kNewSeason>[]>([])
    const selectedSeasonUID = ref<Guid | typeof kNewSeason>(kNewSeason)

    const seasonFormResolver = ReactiveReifiedPromise<{season: MinSeason | null, formData: SeasonFormData}>(undefined, {defaultDebounce_ms: 250})

    const doSubmit = async () : Promise<void> => {
      await GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        const formData = seasonFormResolver.underlying.getOrNull()?.formData;

        if (!formData) {
          return; // shouldn't happen
        }
        if (formData.type === "new") {
          await doSubmitNew()
        }
        else if (formData.type === "edit") {
          await doSubmitEdit()
        }
        else {
          exhaustiveCaseGuard(formData)
        }
      })

      async function doSubmitNew() : Promise<void> {
        const data = seasonFormResolver.underlying.getOrNull()
        if (!data) {
          return // shouldn't happen
        }

        assertIs(data.formData.type, "new")

        try {
          const season = await createSeason(axiosInstance, data.formData.data)

          // insert season into season options as the 2nd option (immediately after the "new" season")
          seasonOptions.value.splice(1, 0, {label: season.season.seasonName, value: season.season.seasonUID});

          // FormKit bug -- Vue is properly reactive WRT to array member methods (e.g. `array.splice`), but FK chooses to only be reactive WRT assignment.
          seasonOptions.value = [...seasonOptions.value]

          selectedSeasonUID.value = season.season.seasonUID;

          iziToast.success({message: "Season created."})

          doReinitFromCurrentSelection()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err);
        }
      }

      async function doSubmitEdit() : Promise<void> {
        const data = seasonFormResolver.underlying.getOrNull()
        if (!data) {
          return // shouldn't happen
        }

        assertIs(data.formData.type, "edit")

        try {
          await updateSeason(axiosInstance, data.formData)
          iziToast.success({message: "Season updated."})
          doReinitFromCurrentSelection()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err);
        }
      }
    }

    onMounted(async () => {
      await GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        const freshSeasonOptions = await listSeasonOptionsForManagerView(axiosInstance);

        seasonOptions.value = [
          {label: "New", value: kNewSeason},
          ...freshSeasonOptions
            .options
            .map(season => {
              return {
                label: season.seasonName,
                value: season.seasonUID
              }
            })
        ]

        const routeDetail = props.routeDetail;

        selectedSeasonUID.value = routeDetail.routeName === "SeasonManager.new"
          ? kNewSeason
          : routeDetail.routeName === "SeasonManager.edit"
          ? seasonOptions.value.find(opt => opt.value === routeDetail.seasonUID)?.value || freshSeasonOptions.defaultSeasonUID
          : freshSeasonOptions.defaultSeasonUID

        doReinitFromCurrentSelection()

        ready.value = true;
      })
    })

    const doReinitFromCurrentSelection = async () : Promise<void> => {
      if (!selectedSeasonUID.value) {
        return // shouldn't happen
      }

      await updateRouteLocation(router, selectedSeasonUID.value);

      if (selectedSeasonUID.value === kNewSeason) {
        seasonFormResolver.run(async () => {
          const seasonInfo = await getSeasonForManagerView(axiosAuthBackgroundInstance, {seasonUID: "new"})
          const formData = freshFormData(seasonInfo.mostRecentSeason);
          return {season: null, formData}
        })
      }
      else {
        seasonFormResolver.run(async () => {
          const seasonInfo = await getSeasonForManagerView(axiosAuthBackgroundInstance, {seasonUID: selectedSeasonUID.value})
          const formData = freshFormDataFromExisting(seasonInfo)
          return {season: seasonInfo.season, formData}
        })
      }
    }

    return () => {
      if (!ready.value) {
        return null;
      }

      return (
        <div data-test="R_SeasonManager" class="mx-auto" style="max-width:1024px; --fk-padding-input:.35em;">
          <h2>Season Manager</h2>
          <div class="mt-2 mb-2">
            Edit existing seasons or add new seasons below. Seasonal settings apply to all programs.
          </div>
          <FormKit
            type="select"
            options={seasonOptions.value}
            v-model={selectedSeasonUID.value}
            onInput={(v: any) => {
              if (typeof v !== "string") {
                unreachable()
              }

              if (selectedSeasonUID.value === v) {
                return;
              }

              selectedSeasonUID.value = v
              doReinitFromCurrentSelection()
            }}
            label="Season"
            data-test="season"
          />
          <div class="my-4">
            {seasonFormResolver.underlying.status === "idle"
              ? null
              : seasonFormResolver.underlying.status === "pending"
              ? <div class="flex items-center gap-2"><SoccerBall/>Loading...</div>
              : seasonFormResolver.underlying.status === "error"
              ? <div>Sorry, something went wrong.</div>
              : (() => {
                const data = seasonFormResolver.underlying.data;
                return <div>
                  <EditSeasonElement
                    key={data.formData.type === "new" ? "new" : data.formData.seasonUID}
                    seasonName={data.formData.type === "new"
                      ? "New Season"
                      : requireNonNull(data.season).seasonName
                    }
                    formData={data.formData}
                    onSubmit={doSubmit}
                  />
                </div>
              })()
            }
          </div>
        </div>
      )
    }
  }
})

function freshFormData(mostRecentSeason: SeasonForManagerView["mostRecentSeason"]) : SeasonFormData {
  //
  // try to find nice defaults for some values
  //
  const calendarSeasonID : CalendarSeason = (mostRecentSeason.calendarSeasonID % 4) + 1; // cycle to next season w/ wrap-around
  const registrationYear = calendarSeasonID < mostRecentSeason.calendarSeasonID
    ? parseIntOrFail(mostRecentSeason.registrationYear) + 1
    : mostRecentSeason.registrationYear;

  return {
    type: "new",
    mostRecentSeason,
    data: {
      seasonName: "",
      seasonYear: dayjs().year(),
      registrationYear: registrationYear,
      calendarSeasonID: calendarSeasonID,
      fullYearCalendar: DobCutoff.noAgeCuttof,
      algorithmSplitBirthYear: "",
      disabled: false,
      inPersonDate: "",
      requireRefDiv: false,
      hasThreeYearOlds: false,
      pointsLockDate: "",
      ratingsPromptDate: "",
      ratingsDate: "",
      profileLockDate: "",
      rosterDate: "",
    }
  }
}

function freshFormDataFromExisting(v: SeasonForManagerView) : SeasonFormData {
  return {
    type: "edit",
    seasonUID: v.season.seasonUID,
    mostRecentSeason: v.mostRecentSeason,
    data: {
      seasonName: v.season.seasonName,
      seasonYear: v.season.seasonYear,
      registrationYear: v.season.registrationYear,
      calendarSeasonID: v.season.calendarSeasonID,
      fullYearCalendar: v.season.fullYearCalendar,
      algorithmSplitBirthYear: v.season.algorithmSplitBirthYear,
      disabled: v.season.disabled,
      inPersonDate: dayjsFormatOr(v.season.inPersonDate, DAYJS_FORMAT_HTML_DATE, ""),
      requireRefDiv: v.season.requireRefDiv,
      hasThreeYearOlds: v.season.hasThreeYearOlds,
      pointsLockDate: dayjsFormatOr(v.season.pointsLockDate, DAYJS_FORMAT_HTML_DATE, ""),
      ratingsPromptDate: dayjsFormatOr(v.season.ratingsPromptDate, DAYJS_FORMAT_HTML_DATE, ""),
      ratingsDate: dayjsFormatOr(v.season.ratingsDate, DAYJS_FORMAT_HTML_DATE, ""),
      profileLockDate: dayjsFormatOr(v.season.profileLockDate, DAYJS_FORMAT_HTML_DATE, ""),
      rosterDate: dayjsFormatOr(v.season.rosterDate, DAYJS_FORMAT_HTML_DATE, ""),
    }
  }
}

async function updateRouteLocation(router: Router, seasonUID: Guid | typeof kNewSeason) {
  if (seasonUID === kNewSeason) {
    await router.replace(routeDetailToRouteLocation({routeName: "SeasonManager.new"}))
  }
  else if (seasonUID) {
    await router.replace(routeDetailToRouteLocation({routeName: "SeasonManager.edit", seasonUID}))
  }
  else {
    // no options ... nothing to do?
    // shouldn't happen
  }
}

const kNewSeason = "new-season"
